import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { StarIcon, CheckIcon, ChevronDownIcon } from 'lucide-react';
import { testimonials, type Testimonial } from '../data/testimonials';
import { Watermark } from './Watermark';
const INITIAL_COUNT = 6;
const trustIndicators = ['Más de 100 kits vendidos', '4.9/5 satisfacción de clientes', 'Envíos a todo Estados Unidos', 'Calidad Premium para profesionales'];
function Stars() {
  return <div className="flex gap-0.5" aria-label="5 de 5 estrellas" data-id="element-335" data-mp="36wmqdseg7y1e">
      {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-3.5 h-3.5 fill-gold text-gold" data-id="element-336" data-mp="2x0tfsekaiem9" data-mpi={i} />)}
    </div>;
}
function TestimonialCard({
  testimonial,
  index
}: {
  testimonial: Testimonial;
  index: number;
}) {
  return <motion.article initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.5,
    delay: index % 6 * 0.06
  }} className="bg-white rounded-xl border border-luxury-black/[0.06] p-7 sm:p-8 flex flex-col h-full" data-id="element-337" data-mp="v96ijv9t1nhc">
      <div className="mb-5" data-id="element-338" data-mp="m5kjyikz0j9s">
        <Stars data-id="element-339" data-mp="m5sysg0nlynm" />
      </div>

      <p className="text-sm sm:text-base text-luxury-dark leading-relaxed mb-7 flex-1" data-id="element-340" data-mp="m2ycsk1zs5s3">
        {testimonial.text}
      </p>

      <div className="pt-5 border-t border-luxury-black/[0.06]" data-id="element-341" data-mp="c6v9havem13k">
        <p className="text-sm font-medium text-luxury-black tracking-wide" data-id="element-342" data-mp="c73pvcqqjoji">
          {testimonial.name}
        </p>
        <p className="text-xs text-gray-400 mt-1 tracking-wide" data-id="element-343" data-mp="zysdwgwixqun">
          {testimonial.location}
        </p>
      </div>
    </motion.article>;
}
export function Testimonials() {
  const [expanded, setExpanded] = useState(false);
  const visible = testimonials.slice(0, INITIAL_COUNT);
  const hidden = testimonials.slice(INITIAL_COUNT);
  return <section className="relative overflow-hidden py-24 px-6 bg-nude-light" data-id="element-344" data-mp="1iu8zn897rug1">
      <Watermark position="right" align="top" size={700} opacity={0.05} data-id="element-345" data-mp="1nmk0qefoxma6" />
      <div className="relative z-10 max-w-6xl mx-auto" data-id="element-346" data-mp="1nnv0m360m9w0">
        {/* Section header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-347" data-mp="1xfwjstff2yyw">
          <div className="flex justify-center mb-4" data-id="element-348" data-mp="1jir7ibp413v7">
            <Stars data-id="element-349" data-mp="1jhy6lrcu18kz" />
          </div>

          <h2 className="font-serif text-3xl sm:text-4xl text-luxury-black mb-3 leading-tight" data-id="element-350" data-mp="1eiv5ghzz3mia">
            Más de 100 profesionales ya elevaron su imagen con Alexa Beauty
          </h2>

          <p className="text-sm sm:text-base text-gray-500 font-sans" data-id="element-351" data-mp="14tbkn6sp9iks">
            4.9/5 basado en más de 100 clientes satisfechos
          </p>

          {/* Trust indicators */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-10 max-w-4xl mx-auto" data-id="element-352" data-mp="10pnduqbw6r0l">
            {trustIndicators.map(item => <div key={item} className="flex items-center justify-center gap-2 bg-white rounded-full border border-luxury-black/[0.06] px-4 py-2.5" data-id="element-353" data-mp="qulmroy23evd">
                <CheckIcon className="w-4 h-4 text-gold shrink-0" data-id="element-354" data-mp="37exp55rbelqj" />
                <span className="text-xs text-luxury-dark text-left" data-id="element-355" data-mp="3cf3iqrh5aib9">
                  {item}
                </span>
              </div>)}
          </div>
        </motion.div>

        {/* First 6 testimonials */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8" data-id="element-356" data-mp="2jr7gl78t3y73">
          {visible.map((t, i) => <TestimonialCard key={t.name} testimonial={t} index={i} data-id="element-357" data-mp="2oq0toyk6ln6q" data-mpi={i} />)}
        </div>

        {/* Expandable remaining testimonials */}
        <AnimatePresence initial={false} data-id="element-358" data-mp="2xht3d71dssa9">
          {expanded && <motion.div key="more-testimonials" initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.5,
          ease: 'easeInOut'
        }} className="overflow-hidden" data-id="element-359" data-mp="2xj4756utu01d">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 pt-6 sm:pt-8" data-id="element-360" data-mp="1gy29d6k2pq4q">
                {hidden.map((t, i) => <TestimonialCard key={t.name} testimonial={t} index={i} data-id="element-361" data-mp="1vo9g4p0pxkpl" data-mpi={i} />)}
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* Toggle button */}
        <div className="flex justify-center mt-12" data-id="element-362" data-mp="24ljskdp9vfw3">
          <motion.button onClick={() => setExpanded(!expanded)} whileHover={{
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} aria-expanded={expanded} className="inline-flex items-center gap-2 border border-luxury-black text-luxury-black font-sans font-medium text-xs tracking-[0.2em] uppercase px-10 py-4 hover:bg-luxury-black hover:text-white transition-colors duration-500" data-id="element-363" data-mp="1zotf7y4uqyed">
            {expanded ? 'Mostrar menos' : 'Ver más testimonios'}
            <motion.span animate={{
            rotate: expanded ? 180 : 0
          }} transition={{
            duration: 0.3
          }} className="flex" data-id="element-364" data-mp="2qvjuxib3wjpu">
              <ChevronDownIcon className="w-4 h-4" data-id="element-365" data-mp="2h609q5zx8drt" />
            </motion.span>
          </motion.button>
        </div>

        {/* Trust line */}
        <motion.p initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="text-center mt-12 text-lg sm:text-xl text-luxury-black font-serif" data-id="element-366" data-mp="2m3scjv23tkas">
          ✨ Más de 100 mujeres ya confían en este kit
        </motion.p>
      </div>
    </section>;
}